import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * Shopify variant IDs are integral; normalize so JSON numeric vs Liquid string guards still match.
 * @param {unknown} a
 * @param {unknown} b
 */
function sameShopifyVariantId(a, b) {
  const sa = String(a == null ? '' : a).trim();
  const sb = String(b == null ? '' : b).trim();
  if (!sa || !sb) return false;
  if (sa === sb) return true;
  try {
    return BigInt(sa) === BigInt(sb);
  } catch {
    return false;
  }
}

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {string} */
  #gspCurrentSegmentSnapshot = '';

  /** Variant id captured together with `#gspCurrentSegmentSnapshot` (guards observer restore). */
  /** @type {string} */
  #gspSnapshotGuardVariantId = '';

  /** @type {MutationObserver | null} */
  #gspLeakObserver = null;

  /** @type {boolean} */
  #suppressGspLeakRestore = false;

  /** Coalesces double-rAF refreshes across rapid variant updates. */
  /** @type {number} */
  #gspLeakRefreshToken = 0;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#deferGspLeakProtectionRefresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardownGspRetailLeakObserver();
    this.#gspLeakRefreshToken += 1;
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * After variant updates / payment widgets, markup may change over several frames.
   * Defer capturing the baseline so we don't treat legitimate updates as "leaks".
   */
  #deferGspLeakProtectionRefresh() {
    const token = ++this.#gspLeakRefreshToken;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (token !== this.#gspLeakRefreshToken) return;
        this.#captureGspSaleSnapshot();
        this.#setupGspRetailLeakObserver();
      });
    });
  }

  #getGspRowLayout() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.price-row-layout[data-product-price-gsp]'));
  }

  #getCurrentPriceSegmentEl() {
    return /** @type {HTMLElement | null} */ (
      this.querySelector('.price-row-layout__current.price') ?? this.querySelector('.price-row-layout__current')
    );
  }

  #captureGspSaleSnapshot() {
    if (this.dataset.pdpGspActive !== 'true') {
      this.#gspCurrentSegmentSnapshot = '';
      this.#gspSnapshotGuardVariantId = '';
      return;
    }
    if (!this.#getGspRowLayout()) {
      this.#gspCurrentSegmentSnapshot = '';
      this.#gspSnapshotGuardVariantId = '';
      return;
    }
    const cur = this.#getCurrentPriceSegmentEl();
    this.#gspCurrentSegmentSnapshot = cur?.innerHTML ?? '';
    this.#gspSnapshotGuardVariantId = String(this.dataset.gspGuardVariantId ?? '');
  }

  #teardownGspRetailLeakObserver() {
    this.#gspLeakObserver?.disconnect();
    this.#gspLeakObserver = null;
  }

  /**
   * Subscription / app scripts sometimes rewrite `.money` after load without `variant:update`.
   * If the discounted row marker is still present, restore the last known good segment markup.
   */
  #setupGspRetailLeakObserver() {
    this.#teardownGspRetailLeakObserver();
    if (this.dataset.pdpGspActive !== 'true') return;

    const layout = this.#getGspRowLayout();
    if (!layout) return;

    this.#gspLeakObserver = new MutationObserver(() => {
      if (this.#suppressGspLeakRestore) return;

      if (
        !sameShopifyVariantId(this.dataset.gspGuardVariantId, this.#gspSnapshotGuardVariantId) ||
        this.#gspSnapshotGuardVariantId === ''
      ) {
        return;
      }

      const stillGsp = this.#getGspRowLayout();
      if (!stillGsp) return;

      const cur = this.#getCurrentPriceSegmentEl();
      if (!cur) return;

      const expected = this.#gspCurrentSegmentSnapshot.trim();
      if (!expected) return;

      const now = cur.innerHTML.trim();
      if (now === expected || now === '') return;

      this.#suppressGspLeakRestore = true;
      try {
        cur.innerHTML = this.#gspCurrentSegmentSnapshot;
      } finally {
        queueMicrotask(() => {
          this.#suppressGspLeakRestore = false;
        });
      }
    });

    this.#gspLeakObserver.observe(layout, { subtree: true, childList: true, characterData: true });
  }

  /**
   * Avoid replacing SSR Google Shopping/channel row with a section fragment that dropped GSP parity
   * (same variant as last good render — common shortly after full page reload).
   *
   * @param {HTMLElement} currentPriceContainer
   * @param {HTMLElement} fetchedPriceContainer
   * @param {VariantUpdateEvent} event
   * @returns {boolean}
   */
  #shouldSkipGspRetailDowngrade(currentPriceContainer, fetchedPriceContainer, event) {
    if (this.dataset.pdpGspActive !== 'true') return false;

    const variantIdRaw = event.detail.resource?.id;
    const guardIdRaw = this.dataset.gspGuardVariantId;
    if (!sameShopifyVariantId(variantIdRaw, guardIdRaw)) return false;

    const domHadMarker = !!currentPriceContainer.querySelector('[data-product-price-gsp]');
    const fetchedHasMarker = !!fetchedPriceContainer.querySelector('[data-product-price-gsp]');
    return domHadMarker && !fetchedHasMarker;
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (this.dataset.pdpGspActive === 'true') {
      this.#teardownGspRetailLeakObserver();
      this.#gspLeakRefreshToken += 1;
      this.#suppressGspLeakRestore = true;
    }

    try {
      const { priceContainer, volumePricingNote } = this.refs;

      const newProductPrice = event.detail.data.html.querySelector(
        `product-price[data-block-id="${this.dataset.blockId}"]`
      );
      if (!newProductPrice) return;

      const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');

      let skippedGspDowngrade = false;
      if (newPrice instanceof HTMLElement && priceContainer instanceof HTMLElement) {
        skippedGspDowngrade = this.#shouldSkipGspRetailDowngrade(priceContainer, newPrice, event);

        if (!skippedGspDowngrade) {
          priceContainer.replaceWith(newPrice);
        }
      }

      if (this.dataset.pdpGspActive === 'true' && event.detail.resource?.id != null) {
        this.dataset.gspGuardVariantId = String(event.detail.resource.id);
      }

      const activePriceContainer = /** @type {HTMLElement | null} */ (this.querySelector('[ref="priceContainer"]'));
      const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

      if (!newNote) {
        volumePricingNote?.remove();
      } else if (!volumePricingNote) {
        activePriceContainer?.insertAdjacentElement(
          'afterend',
          /** @type {Element} */ (newNote.cloneNode(true))
        );
      } else {
        volumePricingNote.replaceWith(newNote);
      }

      const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
      const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
      if (installmentsInput) {
        installmentsInput.value = event.detail.resource?.id ?? '';
        installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } finally {
      if (this.dataset.pdpGspActive === 'true') {
        queueMicrotask(() => {
          this.#suppressGspLeakRestore = false;
        });
        this.#deferGspLeakProtectionRefresh();
      }
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
